import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { appUrl } from "Utils/UrlUtils";
import CancelScheduleSendOutlinedIcon from "@mui/icons-material/CancelScheduleSendOutlined";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import { useCancelEmployeeAccessMutation } from "Api/Mutations/Merchants/Employees/useCancelEmployeeAccessMutation";
import { useRemoveEmployeeMutation } from "Api/Mutations/Merchants/Employees/useRemoveEmployeeMutation";
import { useSendEmployeeAccessMutation } from "Api/Mutations/Merchants/Employees/useSendEmployeeAccessMutation";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import {
  type EmployeeDto,
  EmployeeStatus,
  getAuthIdentityLookup,
  type MerchantDetailDto,
  type UpdateEmployeeRequest,
} from "Infrastructure/Api/Api";
import { Resources, useResource, type TFunction } from "Translations/Resources";
import { useNavigate } from "Navigation/useNavigate";
import { hasApiProblemError } from "Infrastructure/Api/ApiUtils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useUpdateEmployeeMutation } from "Api/Mutations/Merchants/Employees/useUpdateEmployeeMutation";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { PhoneInput } from "Components/Shared/Inputs/Form/PhoneInput";
import { getPhoneSchema } from "Schemas/phoneSchema";
import { toast } from "sonner";
import { EmployeeStatusDropdown } from "Components/Shared/Inputs/Form/EmployeeStatusDropdown";
import { Alert, Stack } from "@mui/material";
import { useState } from "react";

const COMPONENT_RESOURCES = Resources.Employee.Detail;
const HOOK_RESOURCES = Resources.Hooks.Employee;

const employeeSchema = (t: TFunction) =>
  z.object({
    firstName: z.string().min(1, t(Resources.Validation.Required)),
    lastName: z.string().min(1, t(Resources.Validation.Required)),
    email: z.email(t(Resources.Validation.InvalidEmail)),
    phone: getPhoneSchema(t),
    // Read-only in the form; the status follows the invitation lifecycle on the server.
    status: z.enum(EmployeeStatus),
  });

type EmployeeFormValues = z.infer<ReturnType<typeof employeeSchema>>;

const toUpdateRequest = ({
  firstName,
  lastName,
  email,
  phone,
}: EmployeeFormValues): UpdateEmployeeRequest => ({
  firstName,
  lastName,
  email,
  phone,
});

type Props = {
  employee: EmployeeDto;
  currentMerchant: MerchantDetailDto;
};

export const UpdateEmployeeDetailForm = ({
  employee,
  currentMerchant,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useResource();
  const [serverError, setServerError] = useState<string | null>(null);
  const [isCheckingIdentity, setIsCheckingIdentity] = useState(false);
  const {
    isOpen: isCancelDialogOpen,
    open: openCancelDialog,
    handleResult: handleCancelDialogResult,
  } = useConfirmDialog();
  const {
    isOpen: isRemoveDialogOpen,
    open: openRemoveDialog,
    handleResult: handleRemoveDialogResult,
  } = useConfirmDialog();

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = useForm<EmployeeFormValues>({
    resolver: zodResolver(employeeSchema(t)),
    defaultValues: employee,
  });

  const {
    mutate: updateEmployee,
    mutateAsync: updateEmployeeAsync,
    isPending: isUpdatingEmployee,
  } = useUpdateEmployeeMutation({
    onSuccess: (data) => {
      reset(data);
      toast.success(t(Resources.Employee.Detail.SaveSuccess));
    },
    onError: () => toast.error(t(Resources.Employee.Detail.SaveError)),
  });

  const { mutate: sendEmployeeAccess, isPending: isSendingEmployeeAccess } =
    useSendEmployeeAccessMutation({
      onSuccess: () => {
        navigate(appUrl("employees"));
      },
      onError: (error) => {
        if (error.status === 404 && error.data?.errors?.Login) {
          setServerError(t(HOOK_RESOURCES.InviteeNotRegisteredError));
          return;
        }
        // Back-office accounts (Admin, AML, ...) hold a single internal role and can never be
        // invited — a generic "could not be sent" would hide the only fix (use another e-mail).
        if (
          error.status === 403 &&
          hasApiProblemError(error, "BackOfficeTarget")
        ) {
          setServerError(t(HOOK_RESOURCES.BackOfficeTargetError));
          return;
        }
        if (error.status === 409) {
          setServerError(t(HOOK_RESOURCES.EmployeeExistsError));
          return;
        }
        setServerError(t(HOOK_RESOURCES.AccessSendError));
      },
    });

  const { mutate: cancelEmployeeAccess, isPending: isCancellingAccess } =
    useCancelEmployeeAccessMutation();

  const { mutate: removeEmployee, isPending: isRemovingEmployee } =
    useRemoveEmployeeMutation({
      onSuccess: () => {
        navigate(appUrl("employees"));
      },
    });

  const onSubmit = (values: EmployeeFormValues) => {
    updateEmployee({
      data: toUpdateRequest(values),
      publicID: currentMerchant.publicID,
      employeePublicID: employee.publicID,
    });
  };

  // The invite must target the address the merchant sees in the form, not the last saved one:
  // unsaved edits are persisted first, then lookup and send both read the validated form value.
  const handleSendAccess = handleSubmit(async (values) => {
    setServerError(null);

    if (isDirty) {
      try {
        await updateEmployeeAsync({
          data: toUpdateRequest(values),
          publicID: currentMerchant.publicID,
          employeePublicID: employee.publicID,
        });
      } catch {
        // the update mutation already reports the failure
        return;
      }
    }

    setIsCheckingIdentity(true);
    const lookup = await getAuthIdentityLookup(
      values.email,
      currentMerchant.party.publicID ?? undefined,
    );
    setIsCheckingIdentity(false);

    // Only a successful lookup can block the send — when the lookup itself fails,
    // send-access re-validates server-side and reports the same errors.
    if (lookup.status === 200) {
      if (!lookup.data.exists) {
        setServerError(t(HOOK_RESOURCES.InviteeNotRegisteredError));
        return;
      }
      if (lookup.data.isBackOffice) {
        setServerError(t(HOOK_RESOURCES.BackOfficeTargetError));
        return;
      }
      if (lookup.data.hasEmployeeRoleForParty === true) {
        setServerError(t(HOOK_RESOURCES.EmployeeExistsError));
        return;
      }
    }

    sendEmployeeAccess({
      publicID: currentMerchant.publicID,
      employeePublicID: employee.publicID,
      data: { login: values.email },
    });
  });

  const handleCancelAccess = () => {
    cancelEmployeeAccess({
      publicID: currentMerchant.publicID,
      employeePublicID: employee.publicID,
    });
  };

  const handleRemove = () => {
    removeEmployee({
      publicID: currentMerchant.publicID,
      employeePublicID: employee.publicID,
    });
  };

  const isRemoved = employee.status === EmployeeStatus.Inactive;
  const isInvitePending =
    employee.status === EmployeeStatus.InvitedWaitingForConfirmation;
  // A removed employee is invited back through the same button; the e-mail may be a new one.
  const canSendAccess = employee.status === EmployeeStatus.Created || isRemoved;

  return (
    <Stack component="form" onSubmit={handleSubmit(onSubmit)}>
      <FormInput
        label={t(Resources.Common.FirstName)}
        name="firstName"
        control={control}
        fullWidth
      />
      <FormInput
        label={t(Resources.Common.LastName)}
        name="lastName"
        control={control}
        fullWidth
      />
      <PhoneInput
        label={t(Resources.Common.Phone)}
        name="phone"
        control={control}
      />
      <FormInput
        label={t(Resources.Common.Email)}
        name="email"
        type="email"
        control={control}
        fullWidth
        valueTransformer={(v) => v.toLowerCase()}
        disabled={!canSendAccess}
      />
      <EmployeeStatusDropdown
        name="status"
        disabled
        control={control}
        label={t(Resources.Common.Status)}
      />
      {isRemoved && (
        <Alert severity="info">{t(COMPONENT_RESOURCES.RemovedInfo)}</Alert>
      )}
      {serverError && <Alert severity="error">{serverError}</Alert>}
      <Stack gap={3}>
        {canSendAccess && (
          <PrimaryButton
            fullWidth
            isLoading={
              isSendingEmployeeAccess ||
              isCheckingIdentity ||
              isUpdatingEmployee
            }
            onClick={handleSendAccess}
            variant="contained"
            color="primary"
            endIcon={<ForwardToInboxOutlinedIcon />}
          >
            {t(COMPONENT_RESOURCES.SendAccess)}
          </PrimaryButton>
        )}
        {isInvitePending && (
          <PrimaryButton
            fullWidth
            isLoading={isCancellingAccess}
            onClick={openCancelDialog}
            variant="outlined"
            color="error"
            endIcon={<CancelScheduleSendOutlinedIcon />}
          >
            {t(COMPONENT_RESOURCES.CancelInvitation)}
          </PrimaryButton>
        )}
        <PrimaryButton
          fullWidth
          type="submit"
          disabled={!isDirty}
          isLoading={isUpdatingEmployee}
          variant="contained"
          color="primary"
        >
          {t(Resources.Common.Save)}
        </PrimaryButton>
        {!isRemoved && (
          <PrimaryButton
            fullWidth
            isLoading={isRemovingEmployee}
            onClick={openRemoveDialog}
            variant="outlined"
            color="error"
            endIcon={<PersonRemoveOutlinedIcon />}
          >
            {t(COMPONENT_RESOURCES.RemoveEmployee)}
          </PrimaryButton>
        )}
      </Stack>
      <ConfirmDialog
        isDestructive
        isOpen={isCancelDialogOpen}
        modalTitle={t(COMPONENT_RESOURCES.CancelInvitation)}
        modalContent={t(COMPONENT_RESOURCES.CancelInvitationConfirmText)}
        handleResult={handleCancelDialogResult(handleCancelAccess)}
      />
      <ConfirmDialog
        isDestructive
        isOpen={isRemoveDialogOpen}
        modalTitle={t(COMPONENT_RESOURCES.RemoveEmployee)}
        modalContent={t(COMPONENT_RESOURCES.RemoveEmployeeConfirmText)}
        confirmButtonText={t(COMPONENT_RESOURCES.RemoveEmployee)}
        handleResult={handleRemoveDialogResult(handleRemove)}
      />
    </Stack>
  );
};
